// Shared API contract for the recipe scanner (the 5th AI image scanner,
// issue #3709). The client sends one or more photos of a recipe — a printed
// card, a cookbook page, a recipe on a screen, or a handwritten card — and the
// server returns the structured recipe (name, servings, instruction steps, and
// an ingredient list with per-ingredient gram estimates).
//
// Model selection is UNIFIED (issue #3783): the recipe scanner does NOT define
// its own model registry. It reuses the one shared `SCAN_MODELS` registry from
// scan-models.ts on both client and server. We re-export the two model symbols
// here so a consumer can import the params/results types and the model registry
// from a single place. See design-docs/scanner-model-config.md and
// design-docs/recipe-scanner.md.
export { SCAN_MODELS, DEFAULT_SCAN_MODEL_KEY } from './scan-models';

// ── Tunable constants ───────────────────────────────────────────────

/** Maximum images per recipe scan request (multi-page / multi-photo recipes). */
export const MAX_RECIPE_SCAN_IMAGES = 4;

// ── Detected ingredient ─────────────────────────────────────────────

export interface RecipeScanIngredient {
  /** Generic English name optimized for nutrition-DB search (like the meal
   *  scanner's food names), e.g. "all-purpose flour", "olive oil". */
  name: string;
  /** The model's gram estimate for the written quantity (it converts "2 cups
   *  flour" / "1 onion" to grams). */
  estimatedGrams: number;
  /** The quantity text exactly as read from the recipe, e.g. "2 cups", "1 large".
   *  Shown to the user for context; not used for nutrition. */
  rawQuantity: string;
  /** 0..1 confidence in this ingredient line. */
  confidence: number;
}

// ── Status / errors ─────────────────────────────────────────────────

export enum ScanRecipeStatus {
  Success = 'success',
  Blurry = 'blurry',
  NoRecipe = 'no_recipe',
}

/** Reasons the AI returned no usable recipe (the non-Success subset). */
export type NoRecipeReason = ScanRecipeStatus.Blurry | ScanRecipeStatus.NoRecipe;

export enum ScanRecipeError {
  AuthRequired = 'auth_required',
  NoImage = 'no_image',
  TooManyImages = 'too_many_images',
  ImageTooLarge = 'image_too_large',
  AllModelsRestricted = 'all_models_restricted',
  UnknownModel = 'unknown_model',
  ProviderError = 'provider_error',
  ProviderTimeout = 'provider_timeout',
  // the model stopped before returning a complete response — typically the
  // output-token budget was exhausted (a thinking model spent it reasoning).
  // Transient: retrying the same images usually succeeds, so the client keeps
  // the captured photos.
  IncompleteResponse = 'incomplete_response',
}

// ── Quota ───────────────────────────────────────────────────────────

export enum ScanRecipeQuotaRejection {
  AccountLocked = 'account_locked',
  RateLimited = 'rate_limited',
  DailyCooldown = 'daily_cooldown',
  QualityWarning = 'quality_warning',
  // the whole app's daily AI-scan ceiling was reached, not this user's own quota (#4032)
  GlobalCap = 'global_cap',
}

export interface ScanRecipeQuota {
  scansToday: number;
  rejection?: ScanRecipeQuotaRejection;
  cooldownUntil_utc?: string;
  lockedUntil_utc?: string;
}

// ── Client → Server params ──────────────────────────────────────────

export interface ScanRecipeParams {
  imagesBase64: string[];
  model?: string;             // a model key from SCAN_MODELS, or 'all'
  // The user's active UI language (#4206). This scanner's AI output is not
  // localized today: the recipe name / instructions are copied as printed off
  // the photographed card, and each ingredient `name` is deliberately a generic
  // ENGLISH nutrition-database search key. The field is carried so a future
  // framing hook can read `params.locale` without another client release.
  locale?: string;
}

// ── Server → Client results ─────────────────────────────────────────

/** Per-model payload returned when `params.model === 'all'` (multi-model dispatch). */
export interface ScanRecipeModelResult {
  name?: string;
  servings?: number;
  instructions?: string;
  ingredients?: RecipeScanIngredient[];
  // 0-based index of the captured photo that best shows the finished/cooked dish, used
  // as the recipe's image. Undefined when none of the photos shows a cooked dish.
  recipeImageIndex?: number;
  noRecipeReason?: NoRecipeReason;
  // the complete raw text the model returned, before our JSON parsing — kept so the
  // scan log can be diagnosed post-hoc. Carried on this result so it reaches the logger.
  rawResponse?: string;
  durationMs?: number;
  inputTokens?: number;
  outputTokens?: number;
  error?: ScanRecipeError;
}

export interface ScanRecipeResults {
  // single-model response
  name?: string;
  servings?: number;
  instructions?: string;
  ingredients?: RecipeScanIngredient[];
  // 0-based index of the captured photo that best shows the finished/cooked dish (the
  // client uses it as the recipe image). Undefined when no photo shows a cooked dish.
  recipeImageIndex?: number;
  noRecipeReason?: NoRecipeReason;
  modelKey?: string;

  // multi-model response (model='all')
  modelResults?: { [modelKey: string]: ScanRecipeModelResult };
  scanId?: string;

  // quota piggybacks on every scan response
  quota?: ScanRecipeQuota;

  error?: ScanRecipeError;
}
